<template>
    <template v-if="!isMaintenance">
        <div class="app-global-wrapper" :class="{'dark-mode': isDarkMode}">
            <div class="mobile-nav visible-flex-xs hidden-md" v-if="isLoggedIn">
                <div class="logo">
                    <img v-if="isDarkMode" src="./assets/images/logo-alt.svg">
                    <img v-else src="./assets/images/logo.svg">
                </div>
                <div class="actions">
                    <button class="action" @click="isNavOpen = true">
                        <span class="fas fa-bars"></span>
                    </button>
                </div>
            </div>
            <AppNavPanel v-if="isLoggedIn" :isOpen="isNavOpen" @closeNav="closeNav"/>
            <div class="app-content">
                <BreadcrumbsSection v-if="isLoggedIn"/>
                <AppContentSkeleton v-if="isLoggedIn && isPageLoading"/>
                <router-view v-else/>
                <div class="cop-info" v-if="isLoggedIn">
                    <p class="version">v2.1.0</p>
                    <p class="copyright">Design and development by <a href="https://andrii-ilutsa.me" target="_blank">Andrii Ilutsa</a></p>
                </div>
            </div>
        </div>
        <div class="loader-wrapper" :class="{'hidden': !isGlobalLoaderVisible, 'dark-mode': isDarkMode}" v-if="$route.name != 'auth'">
            <div class="loader-stage" role="status" aria-live="polite" aria-label="Loading application">
                <!-- <img v-if="isDarkMode" src="./assets/images/logo-alt.svg" class="logo" alt="IONDS">
                <img v-else src="./assets/images/logo.svg" class="logo" alt="IONDS"> -->
                <div class="loader-panel">
                    <div class="brand-spinner" aria-hidden="true">
                        <span class="spinner-ring"></span>
                        <span class="spinner-orbit"></span>
                        <span class="spinner-core"></span>
                    </div>
                    <p class="loader-text">Loading workspace</p>
                </div>
            </div>
        </div>
    </template>
    <template v-else>
        <div class="maintenance-section">
            <div class="content">
                <div class="image"><img src="./assets/images/maintenance.svg"></div>
                <p class="text">Service on maintenance. We are working and will restore access very soon.</p>
                <p class="text">While we work, you can visit our website</p>
                <div class="actions">
                    <a href="https://about.ionds.app" class="btn">
                        <span class="btn-text">Visit IONDS website</span>
                    </a>
                </div>
            </div>
            <div class="logo"><img src="./assets/images/logo.svg"></div>
        </div>
    </template>
</template>

<script>
import AppNavPanel from './components/AppNavPanel.vue';
import BreadcrumbsSection from './components/BreadcrumbsSection.vue';
import AppContentSkeleton from './components/AppContentSkeleton.vue';

export default {
    data() {
        return {
            isNavOpen: false,
            pageTitle: null,
            isMaintenance: false,
            isGlobalLoaderVisible: false,
            globalLoaderStartedAt: 0,
            globalLoaderTimer: null
        }
    },
    components: {
        AppNavPanel,
        BreadcrumbsSection,
        AppContentSkeleton
    },
    computed: {
        isLoggedIn() {
            return this.$store.getters['isLoggedIn']
        },
        authStatuses() {
            return this.$store.getters['getAuthStatuses'];
        },
        resourceStatuses() {
            return this.$store.getters['getResourceStatuses'];
        },
        isDarkMode() {
            return this.$store.getters['isDarkMode'];
        },
        routeResourceKeys() {
            const dependencies = {
                dashboard: ['users', 'projects', 'dataList'],
                projects: ['users', 'projects'],
                project: ['users', 'projects', 'dataList'],
                datafolders: ['dataList'],
                datastorage: ['dataList'],
                datapage: ['users', 'dataList'],
                newdata: ['users', 'dataList'],
                friends: ['users'],
                users: ['users'],
                logs: ['users', 'projects', 'logs']
            };

            return dependencies[this.$route.name] || [];
        },
        isPageLoading() {
            if (this.authStatuses.loginInProcess || !this.authStatuses.initialDataLoaded) return false;

            return this.routeResourceKeys.some(key => {
                const status = this.resourceStatuses[key];
                return status && !status.loaded;
            });
        }
    },
    methods: {
        closeNav() {
            this.isNavOpen = false;
        },
        ensureRouteResources(route = this.$route) {
            if (!this.authStatuses.initialDataLoaded) return;

            const dependencies = {
                dashboard: ['users', 'projects', 'dataList'],
                projects: ['users', 'projects'],
                project: ['users', 'projects', 'dataList'],
                datafolders: ['dataList'],
                datastorage: ['dataList'],
                datapage: ['users', 'dataList'],
                newdata: ['users', 'dataList'],
                friends: ['users'],
                users: ['users'],
                logs: ['users', 'projects', 'logs']
            };
            const actions = {
                users: 'fetchUsers',
                projects: 'fetchProjects',
                dataList: 'fetchDataList',
                logs: 'fetchLogs'
            };
            const keys = dependencies[route.name] || [];

            keys.forEach(key => {
                const status = this.resourceStatuses[key];
                if (status && !status.loaded && !status.loading) {
                    this.$store.dispatch(actions[key]);
                }
            });
        },
        updateGlobalLoaderVisibility(isLoading) {
            const minLoaderDuration = 2000;

            if (this.globalLoaderTimer) {
                clearTimeout(this.globalLoaderTimer);
                this.globalLoaderTimer = null;
            }

            if (isLoading) {
                this.globalLoaderStartedAt = Date.now();
                this.isGlobalLoaderVisible = true;
                return;
            }

            const elapsed = Date.now() - this.globalLoaderStartedAt;
            const delay = Math.max(minLoaderDuration - elapsed, 0);

            this.globalLoaderTimer = setTimeout(() => {
                this.isGlobalLoaderVisible = false;
                this.globalLoaderTimer = null;
            }, delay);
        }
    },
    watch: {
        '$route': function (to) {
            if (!this.pageTitle) this.pageTitle = document.title;
            document.title = this.pageTitle + ' | ' + to.name.charAt(0).toUpperCase() + to.name.slice(1);
            this.ensureRouteResources(to);
        },
        'authStatuses.initialDataLoaded': function (loaded) {
            if (loaded) this.ensureRouteResources();
        },
        'authStatuses.loginInProcess': {
            immediate: true,
            handler(value) {
                this.updateGlobalLoaderVisibility(value);
            }
        }
    },
    beforeUnmount() {
        if (this.globalLoaderTimer) clearTimeout(this.globalLoaderTimer);
    },
    created() {
        if (!this.isMaintenance) {
            if (localStorage.getItem('pocketbase_auth')) {
                this.$store.dispatch('login');
            } else{
                this.$router.replace('/auth');
            }
            if (localStorage.getItem('theme')) {
                this.$store.dispatch('toggleTheme', (localStorage.getItem('theme') == 'dark'));
            }
        }
    }
}
</script>

<style lang="less">
    @import './assets/less/index.less';
</style>
